import { Directive, ElementRef, Input, NgZone, OnDestroy, OnInit } from '@angular/core';

/**
 * Makes a `dblclick`-gated affordance reachable on a touch screen.
 *
 * Neither engine we ship on dispatches `dblclick` from a touch gesture. WebKit
 * (iOS Safari, and the WKWebView the native app runs in) has never synthesised
 * one — the second tap is claimed by the double-tap-to-zoom gesture recogniser,
 * and switching the element to `touch-action: manipulation` only makes WebKit
 * ignore the double-tap gesture entirely and fire two independent clicks.
 * Chromium on Android behaves the same way (crbug 234986, still open). So every
 * "double click a chip to edit" affordance in the app is unreachable on a phone,
 * on a tablet, and on a touchscreen laptop running Safari — which is why this
 * reproduces in the browser as well as in the native builds.
 *
 * Rather than rewrite ~30 call sites, this directive watches the raw pointer
 * stream and re-dispatches a genuine `dblclick` MouseEvent on the element the
 * finger actually landed on. It bubbles exactly like a mouse one, so the existing
 * `(dblclick)` template bindings keep working unchanged, and so does MatChipRow's
 * own internal `(dblclick)` host listener — the one that drives `[editable]="true"`
 * inline chip editing.
 *
 * Two triggers, because a double-tap is not discoverable on touch:
 *   - two taps inside `DOUBLE_TAP_MS`, landing within `MOVE_TOLERANCE_PX`
 *   - one stationary press held for `LONG_PRESS_MS`
 *
 * Mouse input is left completely alone: a real `dblclick` already arrives there,
 * and a held mouse button is not an edit gesture.
 *
 * Usage — bare attribute, or bound where the affordance is conditional:
 *   <mat-chip-row appTouchEdit (dblclick)="edit(item)">
 *   <mat-chip-row [appTouchEdit]="!hideActions" (dblclick)="edit(item)">
 */

/** Longest gap between two taps that still reads as one double-tap. */
const DOUBLE_TAP_MS = 350;
/** How long a stationary press must be held before it counts as a long press. */
const LONG_PRESS_MS = 500;
/** How far a finger may travel and still be a tap rather than a scroll or a drag. */
const MOVE_TOLERANCE_PX = 12;

/**
 * Descendants that own the touch themselves, so a tap on them is never an edit:
 * the trailing remove button, the inline price input on consumable chips, the
 * drag handle, and the input Material swaps in while a chip is being edited.
 */
const INTERACTIVE_SELECTOR = [
    'button',
    'a',
    'input',
    'textarea',
    'select',
    '[contenteditable]',
    '.mat-chip-edit-input',
    '.cdk-drag-handle'
].join(', ');

@Directive({
    selector: '[appTouchEdit]'
})
export class TouchEditDirective implements OnInit, OnDestroy {

    /**
     * Accepts the bare attribute (empty string) as "on", so `appTouchEdit` alone
     * works, while `[appTouchEdit]="expr"` can switch it off for read-only views.
     */
    @Input('appTouchEdit')
    set appTouchEdit(value: boolean | '') {
        this.enabled = value !== false;
        this.cancel();
    }

    private enabled = true;

    /** The pointer currently being tracked; null when no press is in flight. */
    private pointerId: number | null = null;
    private downX = 0;
    private downY = 0;
    /** The element under the finger on pointerdown — what we dispatch on. */
    private downTarget: EventTarget | null = null;
    private longPressTimer: ReturnType<typeof setTimeout> | null = null;

    /** When and where the previous tap landed, for double-tap detection. */
    private lastTapTime = 0;
    private lastTapX = 0;
    private lastTapY = 0;

    /** Set after a long press so the `click` that follows the lift is swallowed. */
    private swallowNextClick = false;

    private teardown: Array<() => void> = [];

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private ngZone: NgZone,
    ) { }

    ngOnInit(): void {
        // Marks the element for the global `touch-action: manipulation` rule, which
        // stops the browser zooming the page while the user double-taps a chip. A
        // class rather than an inline style on purpose: cdkDrag writes an inline
        // `touch-action: none` on the chips that are drag-reorderable, and that has
        // to keep winning there or dragging breaks on touch.
        this.elementRef.nativeElement.classList.add('touch-edit-target');

        // Outside the zone: a press fires a pointermove per frame and none of them
        // change anything the view binds to. Only the dispatch re-enters the zone.
        this.ngZone.runOutsideAngular(() => {
            const host = this.elementRef.nativeElement;
            this.listen(host, 'pointerdown', this.onPointerDown);
            this.listen(host, 'pointermove', this.onPointerMove);
            this.listen(host, 'pointerup', this.onPointerUp);
            this.listen(host, 'pointercancel', this.onPointerCancel);
            // Capture phase, so the click is stopped before it reaches an ancestor —
            // the list creators wrap their chips in a `mat-form-field` whose own
            // (click) opens the "add" dialog, and a long press must not trip it.
            this.listen(host, 'click', this.onClick, true);
        });
    }

    ngOnDestroy(): void {
        this.cancel();
        this.teardown.forEach(off => off());
        this.teardown = [];
    }

    private listen(
        target: HTMLElement,
        type: string,
        handler: (event: any) => void,
        capture = false
    ): void {
        const bound = handler.bind(this);
        target.addEventListener(type, bound, capture);
        this.teardown.push(() => target.removeEventListener(type, bound, capture));
    }

    private onPointerDown(event: PointerEvent): void {
        // A long press whose click never arrived — the WebView can drop it once a
        // dialog opens over the chip — must not leave the flag armed for the next
        // interaction. Every click is preceded by a pointerdown, so clearing it
        // here bounds the staleness to the gesture that set it.
        this.swallowNextClick = false;

        if (!this.enabled || event.pointerType === 'mouse' || !event.isPrimary) return;
        if (this.isInteractiveTarget(event.target)) return;

        this.pointerId = event.pointerId;
        this.downX = event.clientX;
        this.downY = event.clientY;
        this.downTarget = event.target;

        this.longPressTimer = setTimeout(() => {
            this.longPressTimer = null;
            const target = this.downTarget;
            // The press is spent: the lift must not also count as a tap, and the
            // click the browser sends afterwards must not act on its own.
            this.cancel();
            this.swallowNextClick = true;
            this.lastTapTime = 0;
            this.dispatchDoubleClick(target);
        }, LONG_PRESS_MS);
    }

    private onPointerMove(event: PointerEvent): void {
        if (this.pointerId !== event.pointerId) return;
        // Past the tolerance this is a scroll or a cdkDrag, not a press.
        if (this.distanceFromDown(event) > MOVE_TOLERANCE_PX) {
            this.cancel();
        }
    }

    private onPointerUp(event: PointerEvent): void {
        if (this.pointerId !== event.pointerId) return;

        const target = this.downTarget;
        const moved = this.distanceFromDown(event) > MOVE_TOLERANCE_PX;
        this.cancel();
        if (moved) return;

        const now = Date.now();
        const isSecondTap = now - this.lastTapTime <= DOUBLE_TAP_MS
            && Math.hypot(event.clientX - this.lastTapX, event.clientY - this.lastTapY) <= MOVE_TOLERANCE_PX;

        if (isSecondTap) {
            this.lastTapTime = 0;
            this.dispatchDoubleClick(target);
            return;
        }

        this.lastTapTime = now;
        this.lastTapX = event.clientX;
        this.lastTapY = event.clientY;
    }

    private onPointerCancel(event: PointerEvent): void {
        if (this.pointerId !== event.pointerId) return;
        this.cancel();
    }

    private onClick(event: MouseEvent): void {
        if (!this.swallowNextClick) return;
        this.swallowNextClick = false;
        event.preventDefault();
        event.stopPropagation();
    }

    /** Drops the in-flight press without touching the double-tap history. */
    private cancel(): void {
        if (this.longPressTimer !== null) {
            clearTimeout(this.longPressTimer);
            this.longPressTimer = null;
        }
        this.pointerId = null;
        this.downTarget = null;
    }

    private distanceFromDown(event: PointerEvent): number {
        return Math.hypot(event.clientX - this.downX, event.clientY - this.downY);
    }

    private isInteractiveTarget(target: EventTarget | null): boolean {
        return target instanceof Element && !!target.closest(INTERACTIVE_SELECTOR);
    }

    /**
     * Dispatched on the tapped descendant rather than on the host, so bindings that
     * sit on inner elements — the code, description and date spans of an ICD chip —
     * are reached as well as the ones on the chip itself.
     */
    private dispatchDoubleClick(target: EventTarget | null): void {
        const element = (target instanceof Element ? target : this.elementRef.nativeElement);
        this.ngZone.run(() => {
            element.dispatchEvent(new MouseEvent('dblclick', {
                bubbles: true,
                cancelable: true,
                view: window,
                detail: 2
            }));
        });
    }
}
